/**
 * ActivityLineChart - SVG-based line chart for activity over time
 * Includes work schedule as background when planning is provided
 */

import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { TimelinePoint } from '../types/bureau-activity.types';
import type { TeamMemberWithPlanning, DayOfWeek } from '../../equipe/types/team.types';

interface ActivityLineChartProps {
  data: TimelinePoint[];
  height?: number;
  planning?: TeamMemberWithPlanning | null;
  date?: string; // YYYY-MM-DD, defaults to today
}

// Working hours: 6:00 to 23:30
const WORK_START_HOUR = 6;
const WORK_END_HOUR = 23;
const WORK_END_MINUTE = 30;
const INTERVAL_MINUTES = 5;

const DAY_MAP: Record<number, DayOfWeek> = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday'
};

export const ActivityLineChart: React.FC<ActivityLineChartProps> = ({
  data,
  height = 200,
  planning,
  date
}) => {
  const chartData = useMemo(() => {
    const padding = { top: 20, right: 20, bottom: 40, left: 50 };
    const width = 800;
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Time range in minutes
    const rangeStartMinutes = WORK_START_HOUR * 60;
    const rangeEndMinutes = WORK_END_HOUR * 60 + WORK_END_MINUTE;
    const totalMinutes = rangeEndMinutes - rangeStartMinutes;

    // Convert time string (HH:MM) to x position
    const timeToX = (time: string): number => {
      const [h, m] = time.split(':').map(Number);
      const minutes = h * 60 + m;
      const clampedMinutes = Math.max(rangeStartMinutes, Math.min(rangeEndMinutes, minutes));
      return padding.left + ((clampedMinutes - rangeStartMinutes) / totalMinutes) * chartWidth;
    };

    // Get the target date for planning
    const targetDate = date ? new Date(date) : new Date();
    const dateStr = format(targetDate, 'yyyy-MM-dd');
    const dayKey = DAY_MAP[targetDate.getDay()];

    // Get day planning if available
    const dayPlanning = planning?.planning[dayKey];
    const appointments = planning?.appointments.filter(apt => apt.date === dateStr) || [];

    // Work block (if enabled for this day)
    let workBlock = null;
    if (dayPlanning?.enabled) {
      workBlock = {
        x: timeToX(dayPlanning.start),
        width: timeToX(dayPlanning.end) - timeToX(dayPlanning.start),
        start: dayPlanning.start,
        end: dayPlanning.end,
        isRemote: dayPlanning.workLocation === 'remote'
      };
    }

    // Appointment blocks
    const appointmentBlocks = appointments.map(apt => ({
      x: timeToX(apt.startTime),
      width: timeToX(apt.endTime) - timeToX(apt.startTime),
      title: apt.title,
      start: apt.startTime,
      end: apt.endTime
    }));

    // Current time indicator
    const now = new Date();
    const isToday = format(now, 'yyyy-MM-dd') === dateStr;
    let nowIndicator = null;
    if (isToday) {
      const nowMinutes = now.getHours() * 60 + now.getMinutes();
      if (nowMinutes >= rangeStartMinutes && nowMinutes <= rangeEndMinutes) {
        nowIndicator = padding.left + ((nowMinutes - rangeStartMinutes) / totalMinutes) * chartWidth;
      }
    }

    // Generate full timeline from 6:00 to 23:30 in 5-minute intervals
    const generateFullTimeline = () => {
      const timeline: { time: string; count: number; hour: number; minute: number }[] = [];

      for (let hour = WORK_START_HOUR; hour <= WORK_END_HOUR; hour++) {
        const maxMinute = hour === WORK_END_HOUR ? WORK_END_MINUTE : 55;
        for (let minute = 0; minute <= maxMinute; minute += INTERVAL_MINUTES) {
          timeline.push({
            time: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
            count: 0,
            hour,
            minute
          });
        }
      }
      return timeline;
    };

    const fullTimeline = generateFullTimeline();

    // Create a map of existing data by time (HH:MM)
    // timestamp is already formatted as HH:MM string from backend
    const dataMap = new Map<string, number>();
    if (data && data.length > 0) {
      data.forEach(d => {
        // timestamp is already HH:MM format from backend
        const timeKey = d.timestamp;
        // Accumulate counts for same time bucket
        dataMap.set(timeKey, (dataMap.get(timeKey) || 0) + d.count);
      });
    }

    // Merge data into full timeline
    fullTimeline.forEach(slot => {
      const count = dataMap.get(slot.time);
      if (count !== undefined) {
        slot.count = count;
      }
    });

    const maxCount = Math.max(...fullTimeline.map(d => d.count), 1);

    // Calculate x and y positions
    const totalSlots = fullTimeline.length;
    const points = fullTimeline.map((d, idx) => {
      const x = padding.left + (idx / (totalSlots - 1)) * chartWidth;
      const y = padding.top + chartHeight - (d.count / maxCount) * chartHeight;
      return { x, y, count: d.count, time: d.time, hour: d.hour, minute: d.minute };
    });

    // Create SVG path for line
    let path = '';
    let areaPath = `M ${padding.left} ${padding.top + chartHeight}`;

    points.forEach((p, i) => {
      if (i === 0) {
        path = `M ${p.x} ${p.y}`;
        areaPath += ` L ${p.x} ${p.y}`;
      } else {
        path += ` L ${p.x} ${p.y}`;
        areaPath += ` L ${p.x} ${p.y}`;
      }
    });

    areaPath += ` L ${points[points.length - 1].x} ${padding.top + chartHeight} Z`;

    // Generate time labels every 2 hours
    const timeLabels: { x: number; label: string }[] = [];
    for (let hour = WORK_START_HOUR; hour <= WORK_END_HOUR; hour += 2) {
      const idx = fullTimeline.findIndex(s => s.hour === hour && s.minute === 0);
      if (idx !== -1) {
        timeLabels.push({
          x: points[idx].x,
          label: `${hour}h`
        });
      }
    }

    // Y-axis labels
    const yLabels = [0, Math.ceil(maxCount / 2), maxCount].map(val => ({
      y: padding.top + chartHeight - (val / maxCount) * chartHeight,
      label: val.toString()
    }));

    // Find points with actual activity for highlighting
    const activePoints = points.filter(p => p.count > 0);

    return {
      points,
      activePoints,
      path,
      areaPath,
      maxCount,
      timeLabels,
      yLabels,
      padding,
      chartHeight,
      chartWidth,
      width,
      workBlock,
      appointmentBlocks,
      nowIndicator,
      hasPlanning: !!planning
    };
  }, [data, height, planning, date]);

  const {
    activePoints,
    path,
    areaPath,
    timeLabels,
    yLabels,
    padding,
    chartHeight,
    width,
    workBlock,
    appointmentBlocks,
    nowIndicator,
    hasPlanning
  } = chartData;

  return (
    <div className="w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full min-w-[600px]"
        style={{ maxHeight: height }}
      >
        {/* Definitions */}
        <defs>
          <linearGradient id="activityGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
          </linearGradient>
          <pattern
            id="appointmentStripes"
            patternUnits="userSpaceOnUse"
            width="6"
            height="6"
            patternTransform="rotate(45)"
          >
            <line x1="0" y1="0" x2="0" y2="6" stroke="#ef4444" strokeWidth="3" />
          </pattern>
        </defs>

        {/* Work schedule background (if planning provided) */}
        {workBlock && (
          <rect
            x={workBlock.x}
            y={padding.top}
            width={workBlock.width}
            height={chartHeight}
            fill={workBlock.isRemote ? '#f3e8ff' : '#eff6ff'}
            stroke={workBlock.isRemote ? '#a855f7' : '#3b82f6'}
            strokeWidth="1"
            strokeDasharray="4,4"
            opacity="0.6"
          />
        )}

        {/* Appointment blocks (striped overlay) */}
        {appointmentBlocks.map((apt, i) => (
          <g key={i}>
            <rect
              x={apt.x}
              y={padding.top}
              width={apt.width}
              height={chartHeight}
              fill="url(#appointmentStripes)"
              opacity="0.4"
            />
            <title>{apt.title}: {apt.start} - {apt.end}</title>
          </g>
        ))}

        {/* Grid lines */}
        <g className="grid-lines">
          {yLabels.map((label, i) => (
            <line
              key={i}
              x1={padding.left}
              y1={label.y}
              x2={width - padding.right}
              y2={label.y}
              stroke="#e5e7eb"
              strokeWidth="1"
              strokeDasharray="4,4"
            />
          ))}
        </g>

        {/* Y-axis labels */}
        <g className="y-labels">
          {yLabels.map((label, i) => (
            <text
              key={i}
              x={padding.left - 10}
              y={label.y + 4}
              textAnchor="end"
              fontSize="11"
              fill="#6b7280"
            >
              {label.label}
            </text>
          ))}
        </g>

        {/* X-axis labels */}
        <g className="x-labels">
          {timeLabels.map((label, i) => (
            <text
              key={i}
              x={label.x}
              y={padding.top + chartHeight + 25}
              textAnchor="middle"
              fontSize="11"
              fill="#6b7280"
            >
              {label.label}
            </text>
          ))}
        </g>

        {/* Area fill */}
        <path
          d={areaPath}
          fill="url(#activityGradient)"
          opacity="0.5"
        />

        {/* Activity line */}
        <path
          d={path}
          fill="none"
          stroke="#3b82f6"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Data points - only show dots where there's activity */}
        {activePoints.map((point, i) => (
          <g key={i}>
            <circle
              cx={point.x}
              cy={point.y}
              r="5"
              fill="#3b82f6"
              stroke="white"
              strokeWidth="2"
            />
            <title>
              {point.time}: {point.count} requêtes
            </title>
          </g>
        ))}

        {/* Now indicator */}
        {nowIndicator && (
          <line
            x1={nowIndicator}
            y1={padding.top - 5}
            x2={nowIndicator}
            y2={padding.top + chartHeight + 5}
            stroke="#ef4444"
            strokeWidth="2"
          />
        )}
      </svg>

      {/* Legend - only show if planning is provided */}
      {hasPlanning && (
        <div className="flex items-center gap-4 mt-2 text-[10px] text-gray-500">
          <span className="flex items-center gap-1">
            <span className="w-3 h-2 bg-blue-100 border border-blue-500 border-dashed rounded-sm" /> Horaire bureau
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-2 bg-purple-100 border border-purple-500 border-dashed rounded-sm" /> Télétravail
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-2 rounded-sm" style={{ background: 'repeating-linear-gradient(45deg, #fecaca, #fecaca 2px, transparent 2px, transparent 4px)' }} /> RDV
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-0.5 bg-red-500" /> Maintenant
          </span>
        </div>
      )}
    </div>
  );
};
